/**
 * Cross-tab helpers for the Pal Game.
 *
 * - PalPicker:            select-input rendering the user's pal box;
 *                         shared by every tab that needs a pal target.
 * - ActionWithPal:        generic "pick a pal, hit endpoint, show
 *                         result" card used by Adventure and Expedition.
 * - firePalgamePayout/Defeat: window-event helpers so the shared
 *                         <PayoutFlash> in PalGame.jsx animates payouts
 *                         from any sub-tab without prop-drilling.
 *
 * Behaviour identical to the pre-split monolith — only the file
 * boundary changed.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Currency, Loader, RarityBadge } from "@/components/Atoms";
import { ChilletSkull } from "@/components/ChilletSkull";
import { PalPortrait, ElementChip } from "@/components/PalAssets";
import { Heart } from "lucide-react";


export function firePalgamePayout(amount, label) {
  if (!amount) return;
  window.dispatchEvent(
    new CustomEvent("chillets:palgame-payout", { detail: { amount, label } })
  );
}


export function firePalgameDefeat(amount, label) {
  window.dispatchEvent(
    new CustomEvent("chillets:palgame-defeat", { detail: { amount: amount || 0, label } })
  );
}


export function PalPicker({ value, onChange, pals, testid }) {
  return (
    <select className="input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">Select a Pal…</option>
      {pals.map((p) => <option key={p.pal_name} value={p.pal_name}>{p.pal_name} (Lv {p.level})</option>)}
    </select>
  );
}

// BreedTab — replaces the legacy Fuse tab. Consumes two parents from
// the user's box; cost from palgame settings (`breeding_cost`, default
// 50 skulls). Offspring inherits passives from the union of parent
// passive pools (see `pal_game_stats.roll_passives_for_breed`). Free
// live preview powered by `GET /palgame/breed?pal1=&pal2=`.


export function ActionWithPal({ kind, label, endpoint, testid }) {
  const [pals, setPals] = useState([]);
  const [pal, setPal] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [cd, setCd] = useState(null);
  const [now, setNow] = useState(Date.now());
  const toast = useToast();
  const { refreshProfile } = useAuth();

  useEffect(() => {
    api.get("/palgame/box").then(({ data }) => setPals(data.pals || []));
    const t = setInterval(() => setNow(Date.now()), 1000);
    // Live cooldown updates — admin shortening the catch/battle/adventure
    // cooldown setting should free this card's countdown immediately.
    const onCdUpdate = () => setCd(null);
    window.addEventListener("chillets:cooldown-update", onCdUpdate);
    return () => {
      clearInterval(t);
      window.removeEventListener("chillets:cooldown-update", onCdUpdate);
    };
  }, []);

  const go = async () => {
    if (!pal) return;
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { pal_name: pal });
      setLast(data);
      setCd(new Date(data.cooldown_expires).getTime());
      toast.success(kind === "battle" ? (data.won ? "Victory!" : "Defeat.") : `Earned ${data.reward}`);
      // Battle has a win/loss; Adventure always pays out on success.
      if (data.reward > 0 && (kind !== "battle" || data.won)) {
        firePalgamePayout(
          data.reward,
          kind === "battle" ? "BATTLE WIN" : "ADVENTURE",
        );
      } else if (kind === "battle" && data.won === false) {
        firePalgameDefeat(0, "BATTLE DEFEAT");
      }
      refreshProfile();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.expires_at) setCd(new Date(detail.expires_at).getTime());
      toast.error(detail?.message || apiError(e));
    } finally { setBusy(false); }
  };

  const remain = cd ? Math.max(0, cd - now) : 0;
  const m = Math.floor(remain / 60000);
  const s = Math.floor((remain % 60000) / 1000);

  return (
    <div className="grid lg:grid-cols-2 gap-6 fade-up">
      <div className="glass-card p-7">
        <h3 className="label mb-3">{label}</h3>
        <div className="space-y-3">
          <PalPicker value={pal} onChange={setPal} pals={pals} testid={`${testid}-pal-picker`} />
          <button onClick={go} disabled={busy || !pal || remain > 0} className="btn w-full" data-testid={`${testid}-button`}>
            {remain > 0 ? `Cooldown ${m}:${String(s).padStart(2, "0")}` : busy ? "Going…" : label}
          </button>
        </div>
      </div>
      <div className="glass-card p-7 flex items-center justify-center min-h-[200px]">
        {last ? (
          <div className="text-center w-full" data-testid={`${testid}-result`}>
            {kind === "battle" ? (
              <>
                <div className="text-3xl mb-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
                  {last.won ? <span className="glow-gold">VICTORY</span> : <span className="text-[var(--error)]">Defeat</span>}
                </div>
                <div className="flex items-center justify-center gap-4 my-3">
                  <div className="text-center">
                    <PalPortrait name={last.your_pal} size="w-20 h-20" />
                    <div className="text-[10px] mt-1 truncate max-w-[80px]">{last.your_pal}</div>
                  </div>
                  <span className="text-xs text-[var(--muted)]">VS</span>
                  <div className="text-center">
                    <PalPortrait name={last.opponent} size="w-20 h-20" />
                    <div className="text-[10px] mt-1 truncate max-w-[80px]">{last.opponent}</div>
                    <div className="text-[9px] text-[var(--muted)] uppercase">{last.opponent_rarity}</div>
                  </div>
                </div>
                {last.won && <div className="mt-3"><Currency value={last.reward} /> · {last.xp_gained} XP</div>}
              </>
            ) : (
              <>
                <PalPortrait name={last.pal} size="w-24 h-24 mx-auto mb-3" />
                <div className="text-2xl mb-1 glow-teal">{last.pal}</div>
                <div className="text-sm text-[var(--muted)]">Returned successfully!</div>
                <div className="mt-3"><Currency value={last.reward} /> · {last.xp_gained} XP</div>
              </>
            )}
          </div>
        ) : pal ? (
          (() => {
            // Find the full pal record so we can show level + rarity in the
            // preview, not just the bare name. Helps users decide whether
            // they're picking the right one without opening the detail modal.
            const meta = pals.find((p) => p.pal_name === pal);
            return (
              <div className="text-center w-full" data-testid={`${testid}-preview`}>
                <PalPortrait name={pal} size="w-32 h-32 mx-auto mb-3" />
                <div className="text-xl mb-1.5 glow-teal">{pal}</div>
                {meta && (
                  <div className="flex items-center justify-center gap-2 mb-2 flex-wrap">
                    <span className="text-xs text-[var(--muted)]">Lv {meta.level}</span>
                    <RarityBadge rarity={meta.rarity} />
                    {meta.elements?.slice(0, 3).map((e) => (
                      <ElementChip key={e} element={e} size={12} showLabel={false} />
                    ))}
                  </div>
                )}
                <div className="text-xs text-[var(--muted)]">
                  {remain > 0
                    ? "Resting up — countdown above."
                    : kind === "battle"
                      ? "Ready for the arena."
                      : "Ready for the wilds."}
                </div>
              </div>
            );
          })()
        ) : <p className="text-[var(--muted)] text-sm">Pick a Pal and go.</p>}
      </div>
    </div>
  );
}

